import { Icon, Typography } from '@smartgift/frontend-toolkit';
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { configs, constants } from '@hero/configs';
import { Table } from '@hero/molecules';
import { useGetRecognitionRecipientList } from '@hero/services';
import { getName, getSafeInnerHtml } from '@hero/utils';

import * as S from './style';

function CustomizeMessageTable({
  code,
  customMessages,
  setCustomMessages,
  defaultMessage,
  editable
}) {
  const { t } = useTranslation();
  const [{ pageIndex, pageSize }, setPagination] = useState({
    pageIndex: 0,
    pageSize: 50
  });

  const [globalFilters, setGlobalFilters] = useState({});
  const { data: recipients, isLoading } = useGetRecognitionRecipientList(
    {
      code,
      filters: {
        searchTerm: globalFilters.searchTerm
      },
      pagination: {
        page: pageIndex + 1,
        limit: pageSize
      }
    },
    {
      select(responseData) {
        return {
          ...responseData,
          docs: responseData.docs.map(doc => {
            return {
              ...doc,
              experienceSettings: {
                originalMessage: doc?.experienceSettings?.customizedGiftMessage || defaultMessage,
                customizedGiftMessage: customMessages?.[doc.email]
                  ? customMessages?.[doc.email]?.customizedGiftMessage || defaultMessage
                  : doc?.experienceSettings?.customizedGiftMessage || defaultMessage
              }
            };
          })
        };
      }
    }
  );

  const columns = React.useMemo(
    () => [
      {
        header: t('name'),
        size: 220,
        accessorKey: 'firstName',
        cell: ({ row: { original } }) => (
          <S.TableName role="button" tabIndex={0}>
            <S.TableNameText>{getName(original)}</S.TableNameText>
          </S.TableName>
        )
      },
      {
        id: 'giftMessage',
        header: t('giftMessage'),
        editable: editable,
        size: 320,
        accessorFn: val => {
          return val?.experienceSettings?.customizedGiftMessage;
        },
        type: 'textEditor',
        defaultProps: {
          limit: constants?.general?.giftMessageLimit
        },
        componentProps: {
          label: `${t('giftMessage')}*`
        },
        ...(!editable
          ? { cell: ({ getValue }) => <Typography {...getSafeInnerHtml(getValue())} /> }
          : {})
      },
      {
        id: 'revert',
        header: '',
        cell: ({ row, table }) => {
          const customMessages = table.getState().customMessages;
          const isCustomMessage =
            (customMessages[row.original.email]?.customizedGiftMessage ||
              row.original.experienceSettings?.customizedGiftMessage) !== defaultMessage;

          if (!editable || !isCustomMessage) return null;

          return (
            <S.RevertButton
              size="sm"
              variant="text"
              startIcon={<Icon name="gift-history" />}
              onClick={() => {
                const hasAlreadyCustomMessage =
                  row.original.experienceSettings?.originalMessage !== defaultMessage;

                if (hasAlreadyCustomMessage) {
                  setCustomMessages({
                    ...customMessages,
                    [row.original.email]: {
                      customizedGiftMessage: undefined
                    }
                  });
                } else {
                  delete customMessages[row.original.email];
                  setCustomMessages({
                    ...customMessages
                  });
                }
              }}
            >
              {t('revert')}
            </S.RevertButton>
          );
        }
      }
    ],
    [editable]
  );

  const updateData = (originalRow, columnId, value) => {
    if (value?.trim() !== defaultMessage?.trim()) {
      if (configs?.experience?.customGiftMessageEditorEnabled) {
        const wordArray = value.split('\n');
        wordArray.length = 3;

        setCustomMessages({
          ...customMessages,
          [originalRow.email]: {
            customizedGiftMessage: value
          }
        });
      } else {
        setCustomMessages({
          ...customMessages,
          [originalRow.email]: {
            customizedGiftMessage: value
          }
        });
      }
    }
  };

  return (
    <Table
      columns={columns}
      data={recipients?.docs || []}
      globalFilters={globalFilters}
      state={{
        customMessages
      }}
      pagination={{
        pageIndex,
        pageSize,
        total: recipients?.totalDocs
      }}
      rowHeight={!editable && 52}
      updateData={updateData}
      manualPagination
      loading={isLoading}
      setGlobalFilters={setGlobalFilters}
      setPagination={setPagination}
      pageCount={recipients?.totalPages}
      enabled={{ filter: false, header: true }}
    />
  );
}

export { CustomizeMessageTable };
